import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { getRegimeName, formatCurrency } from '../lib/payrollEngine';
import type { Company, CompanySettings } from '../lib/supabase';

export function SettingsPage() {
  const { company, refreshCompany } = useAuth();
  const [companyForm, setCompanyForm] = useState<Partial<Company>>({});
  const [settings, setSettings] = useState<Partial<CompanySettings>>({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'company' | 'payroll' | 'schedule'>('company');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (company) {
      setCompanyForm({ ...company });
      loadSettings();
    }
  }, [company]);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function loadSettings() {
    // maybeSingle() devuelve null (no error 406) cuando no hay filas
    const { data } = await supabase
      .from('company_settings').select('*')
      .eq('company_id', company!.id)
      .maybeSingle();
    if (data) {
      setSettings(data);
    } else {
      // No hay settings aún — crear defaults y guardarlos
      const defaults = {
        company_id:       company!.id,
        check_in_time:    '08:00:00',
        check_out_time:   '17:00:00',
        tolerance_minutes: 5,
        overtime_rate:    1.25,
        overtime_rate_sunday: 2.00,
        currency:         'PEN',
        timezone:         'America/Lima',
      };
      await supabase.from('company_settings').upsert(defaults, { onConflict: 'company_id' });
      setSettings(defaults);
    }
  }

  async function saveCompany() {
    setSaving(true);
    const { error } = await supabase.from('companies').update(companyForm).eq('id', company!.id);
    if (error) { showToast('Error al guardar', 'error'); }
    else { showToast('Configuración guardada'); await refreshCompany(); }
    setSaving(false);
  }

  async function saveSettings() {
    setSaving(true);
    await supabase.from('company_settings')
      .upsert({ ...settings, company_id: company!.id }, { onConflict: 'company_id' });
    showToast('Configuración de horarios guardada');
    setSaving(false);
  }

  const regimes = [
    {
      value: 'general', label: 'Régimen General', icon: 'business',
      rules: ['30 días de vacaciones por año', 'Gratificación completa (Jul/Dic)', 'CTS completa (1/12 mensual)', 'EsSalud 9%', 'ONP 13% o AFP'],
    },
    {
      value: 'pequeña_empresa', label: 'REMYPE - Pequeña Empresa', icon: 'storefront',
      rules: ['15 días de vacaciones por año', 'Media gratificación (Jul/Dic)', 'CTS al 50%', 'EsSalud 9%', 'ONP 13% o AFP'],
    },
    {
      value: 'microempresa', label: 'REMYPE - Microempresa', icon: 'cottage',
      rules: ['Sin vacaciones obligatorias', 'Sin gratificaciones', 'Sin CTS', 'SIS (referencial)', 'ONP voluntaria'],
    },
  ];

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 animate-fade-in flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-bold ${toast.type === 'success' ? 'bg-green-600' : 'bg-error'}`}>
          <span className="material-symbols-outlined text-[18px]">{toast.type === 'success' ? 'check_circle' : 'error'}</span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Configuración</h1>
        <p className="text-on-surface-variant mt-1">Parámetros de la empresa y reglas del sistema de planillas</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-outline-variant/15">
        {[
          { id: 'company', icon: 'domain', label: 'Perfil de Empresa' },
          { id: 'payroll', icon: 'tune', label: 'Parámetros de Planilla' },
          { id: 'schedule', icon: 'schedule', label: 'Horarios y Asistencia' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            className={`pb-4 flex items-center gap-2 text-sm font-medium transition-all ${
              tab === t.id
                ? 'text-primary font-bold border-b-2 border-primary'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Company ── */}
      {tab === 'company' && (
        <div className="card p-8 space-y-6 animate-fade-in">
          <h3 className="text-xl font-bold font-headline">Información General de la Empresa</h3>
          <div className="grid grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Razón social</label>
              <input className="input-base" value={companyForm.name || ''} onChange={e => setCompanyForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">RUC</label>
              <input className="input-base" value={companyForm.ruc || ''} onChange={e => setCompanyForm(p => ({ ...p, ruc: e.target.value }))} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Dirección</label>
              <input className="input-base" value={companyForm.address || ''} onChange={e => setCompanyForm(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Email</label>
              <input className="input-base" type="email" value={companyForm.email || ''} onChange={e => setCompanyForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Teléfono</label>
              <input className="input-base" value={companyForm.phone || ''} onChange={e => setCompanyForm(p => ({ ...p, phone: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t border-outline-variant/10">
            <button onClick={saveCompany} disabled={saving} className="btn-primary text-white px-8 py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-60 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">save</span>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      )}

      {/* ── Tab: Payroll params ── */}
      {tab === 'payroll' && (
        <div className="space-y-6 animate-fade-in">
          {/* Regime selector */}
          <div className="card p-8">
            <h3 className="text-xl font-bold font-headline mb-2">Régimen Laboral</h3>
            <p className="text-sm text-on-surface-variant mb-6">
              <strong className="text-error">⚠ Importante:</strong> Cambiar el régimen afectará todos los cálculos futuros de planillas. Los beneficios laborales se calcularán automáticamente según el régimen elegido.
            </p>
            <div className="space-y-3">
              {regimes.map(r => (
                <label key={r.value} className={`flex gap-4 p-5 rounded-xl cursor-pointer border-2 transition-all ${
                  companyForm.regime === r.value ? 'border-primary bg-primary/5' : 'border-outline-variant/20 hover:border-primary/30 hover:bg-surface-container-low'
                }`}>
                  <input type="radio" name="regime" value={r.value} checked={companyForm.regime === r.value} onChange={e => setCompanyForm(p => ({ ...p, regime: e.target.value as any }))} className="mt-1 accent-primary" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="material-symbols-outlined text-primary text-[20px]">{r.icon}</span>
                      <p className="font-bold text-on-surface">{r.label}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.rules.map(rule => (
                        <span key={rule} className="text-xs bg-surface-container-low text-on-surface-variant px-2 py-0.5 rounded-full">{rule}</span>
                      ))}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Numeric params */}
          <div className="card p-8">
            <h3 className="text-xl font-bold font-headline mb-2">Parámetros Numéricos</h3>
            <p className="text-sm text-on-surface-variant mb-6">
              Todos los porcentajes se aplican automáticamente en cada planilla procesada.
            </p>
            <div className="grid grid-cols-3 gap-5">
              {[
                { label: 'UIT (S/.)', field: 'uit', help: 'Unidad Impositiva Tributaria vigente. 2025: S/ 5,350 · 2026: S/ 5,500 (verificar DS)' },
                { label: 'RMV (S/.)', field: 'rmv', help: 'Remuneración Mínima Vital vigente. Actualizar cuando cambie.' },
                { label: 'EsSalud (%)', field: 'essalud_rate', help: 'Aporte del empleador. Estándar: 9%' },
                { label: 'Días laborables/mes', field: 'working_days_per_month', help: 'Días laborables del mes (sin domingos). Ej: 26' },
                { label: 'Horas diarias', field: 'daily_work_hours', help: 'Horas de jornada laboral por día. Estándar: 8' },
              ].map(param => (
                <div key={param.field} className="bg-surface-container-low p-5 rounded-xl border border-outline-variant/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-on-surface-variant uppercase tracking-tighter">{param.label}</span>
                    <span className="material-symbols-outlined text-primary text-[18px]">edit</span>
                  </div>
                  <input
                    className="text-2xl font-bold bg-transparent border-none w-full p-0 focus:ring-0 outline-none text-on-surface"
                    type="number"
                    step="0.01"
                    value={(companyForm as any)[param.field] || 0}
                    onChange={e => setCompanyForm(prev => ({ ...prev, [param.field]: parseFloat(e.target.value) || 0 }))}
                  />
                  <p className="text-[10px] mt-1.5 text-on-surface-variant">{param.help}</p>
                </div>
              ))}

              {/* Sistema pensionario + tasa */}
              <div className="bg-surface-container-low p-5 rounded-xl border border-outline-variant/10">
                <span className="text-xs font-bold text-on-surface-variant uppercase tracking-tighter block mb-3">Sistema Pensionario</span>
                <select
                  className="text-base font-bold bg-transparent border-none w-full p-0 focus:ring-0 outline-none mb-2"
                  value={companyForm.afp_system || 'onp'}
                  onChange={e => {
                    const sys = e.target.value as any;
                    // Al cambiar sistema, sugerir tasa default
                    setCompanyForm(p => ({ ...p, afp_system: sys, snp_rate: sys === 'onp' ? 13 : p.snp_rate }));
                  }}
                >
                  <option value="onp">ONP</option>
                  <option value="afp">AFP</option>
                </select>
                <p className="text-[10px] text-on-surface-variant">ONP fijo 13% · AFP varía según entidad</p>
              </div>

              {/* Tasa ONP/AFP — campo separado y claro */}
              <div className="bg-primary/5 p-5 rounded-xl border-2 border-primary/20">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-primary uppercase tracking-tighter">
                    {companyForm.afp_system === 'afp' ? 'Tasa AFP (%)' : 'Tasa ONP (%)'}
                  </span>
                  <span className="material-symbols-outlined text-primary text-[18px]">percent</span>
                </div>
                <input
                  className="text-2xl font-bold bg-transparent border-none w-full p-0 focus:ring-0 outline-none text-primary"
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={companyForm.snp_rate || 13}
                  onChange={e => setCompanyForm(prev => ({ ...prev, snp_rate: parseFloat(e.target.value) || 0 }))}
                />
                <p className="text-[10px] mt-1.5 text-primary/70">
                  {companyForm.afp_system === 'afp'
                    ? 'Tasa fija legal: 10% aporte obligatorio + 1.37% prima seguro = 11.37% total'
                    : 'ONP: fijo 13% sobre remuneración bruta asegurable'}
                </p>
              </div>
            </div>

            {/* Assign familiar toggle */}
            <div className="mt-5 flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/15">
              <div>
                <p className="font-bold text-sm text-on-surface">Asignación Familiar</p>
                <p className="text-xs text-on-surface-variant">10% de la RMV para trabajadores con hijos (No aplica en Microempresa)</p>
              </div>
              <button
                onClick={() => setCompanyForm(p => ({ ...p, assign_familiar: !p.assign_familiar }))}
                className={`w-12 h-6 rounded-full relative transition-all ${companyForm.assign_familiar ? 'bg-primary' : 'bg-surface-container-highest'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${companyForm.assign_familiar ? 'right-0.5' : 'left-0.5'}`} />
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={saveCompany} disabled={saving} className="btn-primary text-white px-8 py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-60 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">save</span>
              {saving ? 'Guardando...' : 'Guardar parámetros'}
            </button>
          </div>
        </div>
      )}

      {/* ── Tab: Schedule ── */}
      {tab === 'schedule' && (
        <div className="card p-8 space-y-6 animate-fade-in">
          <h3 className="text-xl font-bold font-headline">Horarios de Trabajo</h3>
          <p className="text-sm text-on-surface-variant">Define los horarios base para el cálculo de tardanzas y horas extra</p>
          <div className="grid grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Hora de entrada</label>
              <input className="input-base" type="time" value={settings.check_in_time?.slice(0, 5) || '08:00'} onChange={e => setSettings(p => ({ ...p, check_in_time: e.target.value + ':00' }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Hora de salida</label>
              <input className="input-base" type="time" value={settings.check_out_time?.slice(0, 5) || '17:00'} onChange={e => setSettings(p => ({ ...p, check_out_time: e.target.value + ':00' }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Tolerancia para tardanza (minutos)</label>
              <input className="input-base" type="number" min="0" max="30" value={settings.tolerance_minutes || 5} onChange={e => setSettings(p => ({ ...p, tolerance_minutes: +e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Multiplicador horas extra (x)</label>
              <input className="input-base" type="number" step="0.05" min="1" value={settings.overtime_rate || 1.25} onChange={e => setSettings(p => ({ ...p, overtime_rate: parseFloat(e.target.value) }))} />
            </div>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            <strong>Nota legal:</strong> Según DS 007-2002-TR, las horas extra se pagan con recargo mínimo del 25% para las 2 primeras horas y 35% para las siguientes.
          </div>
          <div className="flex justify-end pt-4 border-t border-outline-variant/10">
            <button onClick={saveSettings} disabled={saving} className="btn-primary text-white px-8 py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-60 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">save</span>
              {saving ? 'Guardando...' : 'Guardar horarios'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
